import { useContext, useRef } from "react";
import clsx from "clsx";
import { useLoaderData } from "react-router-dom";
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import PageTransition from "./templateParts/PageTransition";
import Label from "@/components/utils/Label/Label";
import { DESKTOP } from "@/utils/appConstants";
import useUtils from "@/hooks/useUtils";
import "./singlePostStyles.scss";
import { removeHtmlTags } from "./SinglePostUtils";
import { Breadcrumb } from "./utils/Breadcrumb";
import useReadingProgress from "@/hooks/useReadingProgress";
import userIcon from "@/assets/img/user_icon.svg";
import verifiedIcon from "@/assets/img/verified_icon.svg";
import facebookIcon from "@/assets/img/facebook_social_icon.svg";
import linkedinIcon from "@/assets/img/linkedin_social_icon.svg";
import nsIcon from "@/assets/img/icon_ns.svg";
import xIcon from "@/assets/img/x_social_icon.svg";
import RelatedArticles from "@/components/modules/RelatedArticles/RelatedArticles.jsx";
import Shortcode from "./modules/Shortcode/Shortcode";
import { useLocation, useNavigate } from "react-router-dom";
import { context } from "@/Layout";

const SinglePost = () => {
  const { device } = useUtils();
  const [isMobile, setIsMobile] = useState();

  const { lenisInstance } = useContext(context);

  useEffect(() => {
    setIsMobile(device != DESKTOP);
  }, [device]);

  const data = useLoaderData();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Never canonicalize the URL while previewing a draft — it would replace
    // the preview query string (?p=ID&preview=true) with a normal /insights/
    // path, and reloading that path drops preview.php's auth context.
    const isPreview =
      window.__NS_PREVIEW__ && new URLSearchParams(location.search).get("preview") === "true";
    if (isPreview) return;

    const pathname = location.pathname;

    if (pathname.startsWith("/insights/")) return;

    if (!data) return;

    const categories = data.categories_info;
    const slug = data.slug;

    if (categories && categories.length > 0 && slug) {
      const categorySlug = categories[0].slug;
      const newUrl = `/insights/${categorySlug}/${slug}`;

      navigate(newUrl, { replace: true });
    }
  }, [data, location.pathname, navigate]);

  const progressBarRef = useRef(null);
  const { completed: progressCompleted, getProgressBarClasses } =
    useReadingProgress(progressBarRef);

  const progressBarClasses = clsx(
    "progress-bar-container h-1 transition-all duration-100 ease-in-out left-0 w-full z-[3]",
    getProgressBarClasses()
  );

  const {
    categories_info: categories,
    content,
    excerpt,
    featured_image: featuredImage,
    tags_info: tags,
    title,
    blog_author: author,
  } = data;

  const currentURL = window.location.href;
  const socialTitle = removeHtmlTags(title.rendered);
  const socialSummary = removeHtmlTags(excerpt.rendered);
  const shortcodeDesktop = data?.acf?.sidebar_shortcode || "";
  const shortcodeMobile = data?.acf?.mobile_shortcode || "";

  const extractHeadings = (htmlContent) => {
    if (!htmlContent) return [];

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    const headings = [];
    let lastH2Index = -1;
    let lastH3Index = -1;

    tempDiv.querySelectorAll("h2, h3, h4").forEach((heading) => {
      const text = heading.textContent.trim();
      let id = heading.id;

      if (!id) {
        id = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        heading.id = id;
      }

      if (heading.tagName === "H2") {
        lastH2Index++;
        lastH3Index = -1;
        headings.push({ id, text, subheadings: [] });
      } else if (heading.tagName === "H3" && lastH2Index >= 0) {
        lastH3Index++;
        headings[lastH2Index].subheadings.push({ id, text, subsubheadings: [] });
      } else if (heading.tagName === "H4" && lastH2Index >= 0 && lastH3Index >= 0) {
        headings[lastH2Index].subheadings[lastH3Index].subsubheadings.push({ id, text });
      }
    });

    return headings;
  };

  const addHeadingIds = (htmlContent) => {
    if (!htmlContent) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    tempDiv.querySelectorAll("h2, h3, h4").forEach((heading) => {
      const text = heading.textContent.trim();
      let id = heading.id;

      if (!id) {
        id = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        heading.id = id;
      }
    });

    return tempDiv.innerHTML;
  };

  const updatedContent = addHeadingIds(DOMPurify.sanitize(content.rendered));

  const handleOnClick = (evt) => {
    evt.preventDefault();

    const target = evt.target;

    const href = target.getAttribute("href");
    const element = document.getElementById(href.substring(1));

    if (lenisInstance) {
      if (element) {
        lenisInstance.scrollTo(element, {
          immediate: false,
          offset: -50,
        });
      }
      return;
    }

    const getHeaderOffset = () => document.querySelector("[data-sticky-header]")?.offsetHeight ?? 0;

    // mobile fallback
    const rect = element.getBoundingClientRect();
    const top = rect.top + window.scrollY - getHeaderOffset();

    window.scrollTo({
      top,
      behavior: "auto",
    });
  };

  return (
    <PageTransition>
      <section className="single-post">
        <div className="full-container px-0">
          <div className="banner-container" ref={progressBarRef}>
            <div className={`${progressBarClasses} bg-tertiaryGray`}>
              <span
                className="h-1 bg-watermelon block origin-left transition-transform duration-200 ease-linear"
                style={{ transform: `translateX(${progressCompleted - 100}%)` }}
              />
            </div>
            <img className="image-background" src={featuredImage.large} alt={featuredImage.alt} />
            <div className="info-container">
              <Breadcrumb categories={categories} title={title.rendered} />
              <div className="title-container">
                <h1>{title.rendered}</h1>
              </div>

              {categories && (
                <div className="meta-container">
                  {categories.map((category, index) => (
                    <Label
                      className="label-primary inline-block text-white"
                      key={index}
                      text={category.name}
                      isSmall={false}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="overlay" />
          </div>
        </div>

        <div className="content-container container-revamp xl:px-0">
          <div
            className={`md:flex md:flex-column sm:flex-row flex-wrap grow-0 items-start ${shortcodeDesktop || shortcodeMobile ? "lg:w-full" : "lg:w-full lg:pr-10"}`}
          >
            <div className="lg:w-[30%] lg:pr-10 lg:block w-full relative">
              <div className="top-4 left-4 text-medium font-public text-[24px] font-bold lg:left-0">
                Content
              </div>
              <div className="sidebar mb-0 overflow-hidden relative transition-all duration-300 ease-in-out">
                <div className="dropdown-content pt-4">
                  <ul className="text-medium font-public text-lg">
                    {extractHeadings(content.rendered).map((section, index) => (
                      <li key={index}>
                        <a
                          href={`#${section.id}`}
                          className="font-medium text-medium"
                          onClick={handleOnClick}
                        >
                          {section.text}
                        </a>
                        {section.subheadings.length > 0 && (
                          <ul className="space-y-1 list-disc pl-7 pt-2">
                            {section.subheadings.map((sub, subIndex) => (
                              <li key={subIndex} className="font-medium text-medium">
                                <a href={`#${sub.id}`} className="text-medium">
                                  {sub.text}
                                </a>
                                {sub.subsubheadings.length > 0 && (
                                  <ul className="space-y-1 list-disc pl-7 pt-2">
                                    {sub.subsubheadings.map((subsub, subsubIndex) => (
                                      <li key={subsubIndex}>
                                        <a href={`#${subsub.id}`} className="text-medium">
                                          {subsub.text}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="lg:block lg:w-full flex flex-col items-start mt-8">
                  <h3 className="text-white font-public text-[24px] font-bold mb-4">Share</h3>
                  <ul className="flex gap-6">
                    <li>
                      <a
                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentURL}&title=${socialTitle}&summary=${socialSummary}`}
                      >
                        <img
                          aria-label="LinkedIn Share Icon"
                          className="w-6 h-6 lg:w-8 lg:h-8"
                          src={linkedinIcon}
                          title="Share to LinkedIn"
                        />
                      </a>
                    </li>
                    <li>
                      <a
                        href={`https://x.com/intent/tweet?url=${currentURL}&text=${socialTitle}%0A${socialSummary}`}
                      >
                        <img
                          aria-label="X Icon"
                          className="w-6 h-6 lg:w-8 lg:h-8"
                          src={xIcon}
                          title="Share to X"
                        />
                      </a>
                    </li>
                    <li>
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${currentURL}`}>
                        <img
                          aria-label="Facebook Icon"
                          className="w-6 h-6 lg:w-8 lg:h-8"
                          src={facebookIcon}
                          title="Share to Facebook"
                        />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {(shortcodeDesktop || shortcodeMobile) && (
                <div className="w-full mt-8 mb-11 flex justify-center items-center">
                  {!isMobile ? (
                    <Shortcode shortcode={shortcodeDesktop} />
                  ) : (
                    <Shortcode shortcode={shortcodeMobile} />
                  )}
                </div>
              )}
            </div>

            <div className="w-full mt-8 lg:mt-0 lg:w-[70%]">
              {author && (
                <div className="author-info flex gap-5 mb-16">
                  <div>
                    <img
                      className="author-profile-img w-[76px] h-[76px] object-cover object-center rounded-3xl"
                      src={author?.author_profile_img?.url || nsIcon}
                      alt={author?.author_profile_img?.alt || "Nicasource"}
                    />
                  </div>
                  <div className="w-3/4 xl:w-11/12">
                    <h2 className="text-base font-black text-white mb-2 xl:text-xl">{`By ${author.author_name || "Nicasource"}`}</h2>
                    <div
                      className="text-white text-xs font-normal xl:text-base"
                      dangerouslySetInnerHTML={{
                        __html: author?.author_description || "Your nearshore engineering partner",
                      }}
                    />
                  </div>
                </div>
              )}
              <div
                className="paragraph-content w-full max-w-none"
                dangerouslySetInnerHTML={{ __html: updatedContent }}
              />

              {author ? (
                <div className="author-info bg-darkGrayRevamp rounded-[32px] p-6 flex flex-col mt-16">
                  <div className="flex gap-5">
                    <div className="flex flex-col items-center justify-center w-[100px] h-[100px] rounded-3xl grow-0 shrink-0 overflow-hidden">
                      <img
                        className="author-profile-img object-cover object-center"
                        src={author?.author_profile_img?.url || nsIcon}
                        alt={author?.author_profile_img?.alt || "Nicasource"}
                      />
                    </div>

                    <div className="flex flex-col h-fit">
                      <div className="flex flex-col mb-2 gap-2 h-fit xl:gap-4 xl:flex-row">
                        <h2 className="text-xl font-black text-white">{`By ${author.author_name || "Nicasource"}`}</h2>
                        {author?.author_verification && (
                          <div className="verification-info flex items-center gap-2">
                            <img
                              className="verification-icon w-5 h-5"
                              src={verifiedIcon}
                              alt="verified-icon"
                            />
                            <span className="text-gold text-base font-medium">Verified Expert</span>
                          </div>
                        )}
                        {author?.author_experience && (
                          <div className="verification-info flex items-center gap-2">
                            <img
                              className="verification-icon w-5 h-5"
                              src={userIcon}
                              alt="user-icon"
                            />
                            <span className="text-base text-quaternary font-medium">
                              {author?.author_experience}{" "}
                              {parseInt(author?.author_experience, 10) === 1 ? "Year" : "Years"} of
                              Experience
                            </span>
                          </div>
                        )}
                      </div>

                      <div
                        className="text-white font-normal hidden xl:flex"
                        dangerouslySetInnerHTML={{
                          __html:
                            author?.author_description || "Your nearshore engineering partner",
                        }}
                      />
                    </div>
                  </div>

                  <div className="xl:pl-[120px]">
                    <div
                      className="text-white font-normal flex pt-5 xl:pb-4 xl:hidden"
                      dangerouslySetInnerHTML={{
                        __html: author?.author_description || "Your nearshore engineering partner",
                      }}
                    />
                    {author?.author_expertise && (
                      <div className="mt-4">
                        <span className="text-xl text-white font-normal">Expertise:</span>
                        <ul className="expertise-list flex gap-4 flex-wrap mt-4">
                          {author?.author_expertise.map((expertise, index) => (
                            <li
                              key={index}
                              className="text-white text-base font-normal p-2 border border-white rounded-2xl"
                            >
                              {expertise?.author_expertise_name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {author?.author_role && (
                      <div className="mt-4 flex flex-col gap-3">
                        <span className="text-xl text-white font-normal">Role:</span>
                        <span className="text-white font-normal text-base">
                          {author.author_role}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="author-info flex flex-col mt-16">
                  <div className="flex gap-5">
                    <div className="flex flex-col items-center justify-center w-[100px] h-[100px] rounded-3xl grow-0 shrink-0 overflow-hidden">
                      <img
                        className="author-profile-img object-cover object-center"
                        src={nsIcon}
                        alt={"Nicasource"}
                      />
                    </div>

                    <div className="flex flex-col h-fit">
                      <h2 className="text-xl font-black text-white">Nicasource</h2>
                      <div className="text-white font-normal hidden xl:flex">
                        Your nearshore engineering partner
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tags && (
                <div className="meta-container mt-12">
                  {tags.map((tag, index) => (
                    <Label
                      className="label-primary text-white mb-4 inline-block"
                      key={index}
                      text={tag.name}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <RelatedArticles />
      </section>
    </PageTransition>
  );
};

export default SinglePost;
